/**
 * Cross-Tab Auth Signal Utility (P0 SECURITY + UX)
 * 
 * CRITICAL: Prevents stale contractor data from displaying after auth changes.
 * 
 * Key features:
 * 1. Monotonic auth version (localStorage source of truth)
 * 2. BroadcastChannel for instant cross-tab communication
 * 3. hardAuthReset() that purges ALL cached data (auth + domain data)
 * 4. Runtime identity mismatch detection
 * 
 * When auth changes in ANY tab, all other tabs MUST:
 * - Purge all cached app data (customers/jobs/estimates/invoices/etc)
 * - Redirect to correct shell (tech -> /tech, contractor -> /dashboard)
 */

const CHANNEL_NAME = 'fsp-auth';
const AUTH_VERSION_KEY = 'fsp_auth_version';
const AUTH_IDENTITY_KEY = 'fsp_auth_identity';
const STORAGE_KEY = 'fsp_auth_changed_at'; // legacy, still used for payload

// Domain data cache keys to purge on auth change
const DOMAIN_CACHE_KEYS = [
  'customers_cache',
  'jobs_cache', 
  'estimates_cache',
  'invoices_cache',
  'technicians_cache',
  'visits_cache',
  'calendar_cache',
  'settings_cache',
  'tenant_cache',
  'entitlements_cache',
  'follow_ups_cache',
  'reports_cache',
];

// Auth-related keys to purge
const AUTH_CACHE_KEYS = [
  'auth_token',
  'token',
  'user',
  'tenant',
  'fsp_auth_identity',
];

// Unique identifier for THIS browser tab - used to filter out self-sent BroadcastChannel messages
// BroadcastChannel.postMessage() delivers to ALL subscribers including the sender,
// which can cause a destructive loop where login triggers auth reset in the same tab.
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;

let broadcastChannel = null;
let currentAuthVersion = null;
let lastKnownIdentity = null;

// Initialize BroadcastChannel if available
try {
  if (typeof BroadcastChannel !== 'undefined') {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
  }
} catch (e) {
  console.log('[AuthSignal] BroadcastChannel not available, using localStorage fallback');
}

/**
 * Initialize the auth signal system
 * Should be called once at app bootstrap
 */
export function initAuthSignal() {
  // Read current auth version
  currentAuthVersion = localStorage.getItem(AUTH_VERSION_KEY);
  
  // Read last known identity
  try {
    const storedIdentity = localStorage.getItem(AUTH_IDENTITY_KEY);
    if (storedIdentity) {
      lastKnownIdentity = JSON.parse(storedIdentity);
    }
  } catch (e) {
    lastKnownIdentity = null;
  }
  
  console.log('[AuthSignal] Initialized, version:', currentAuthVersion);
  return currentAuthVersion;
}

/**
 * Bump the auth version (monotonically increasing)
 * This is the SOURCE OF TRUTH for auth changes
 * @param {string} reason - Why auth changed: 'login' | 'logout' | 'activate' | 'session_switch'
 * @param {object} identity - Optional { email, role } of the new identity
 */
export function bumpAuthVersion(reason, identity = null) {
  const newVersion = String(Date.now());
  const payload = {
    version: newVersion,
    reason,
    identity,
    timestamp: Date.now(),
    senderId: TAB_ID,  // Include sender ID to filter out self-sent messages
  };

  console.log('[AuthSignal] Bumping auth version:', reason, newVersion, 'from tab:', TAB_ID);

  // Update localStorage (source of truth)
  try {
    localStorage.setItem(AUTH_VERSION_KEY, newVersion);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    
    // Store identity for mismatch detection
    if (identity) {
      localStorage.setItem(AUTH_IDENTITY_KEY, JSON.stringify(identity));
      lastKnownIdentity = identity;
    } else {
      localStorage.removeItem(AUTH_IDENTITY_KEY);
      lastKnownIdentity = null;
    }
  } catch (e) {
    console.error('[AuthSignal] Failed to update localStorage:', e);
  }

  // Also broadcast via BroadcastChannel for instant notification
  // Note: BroadcastChannel delivers to ALL tabs including sender,
  // so receivers must filter by senderId to avoid self-processing
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage(payload);
    } catch (e) {
      console.error('[AuthSignal] BroadcastChannel failed:', e);
    }
  }

  currentAuthVersion = newVersion;
  return newVersion;
}

/**
 * CRITICAL: Hard reset all app state
 * Purges ALL cached data - auth AND domain data (customers/jobs/estimates/etc)
 * Must be called before any redirect on auth change
 */
export function hardAuthReset() {
  console.log('[AuthSignal] ⚠️ HARD AUTH RESET - Purging all cached data');

  // 1. Clear all auth-related localStorage keys
  AUTH_CACHE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`[AuthSignal] Failed to clear ${key}:`, e);
    }
  });

  // 2. Clear all domain data cache keys
  DOMAIN_CACHE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`[AuthSignal] Failed to clear ${key}:`, e);
    }
  });

  // 3. Clear all sessionStorage
  try {
    sessionStorage.clear();
  } catch (e) {
    console.error('[AuthSignal] Failed to clear sessionStorage:', e);
  }

  // 4. Clear any in-memory caches
  // Note: React Query, SWR, or other cache libraries should be reset
  // This is handled by the page reload, but we mark the need
  window.__FSP_AUTH_RESET_PENDING__ = true;

  // 5. Reset identity tracking
  lastKnownIdentity = null;

  console.log('[AuthSignal] ✓ All caches purged');
}

/**
 * Check if identity has changed (runtime mismatch detection)
 * @param {object} currentUser - Current user from /api/auth/me { email, role }
 * @returns {boolean} True if identity mismatch detected
 */
export function checkIdentityMismatch(currentUser) {
  if (!currentUser || !currentUser.email) {
    return false;
  }

  // Update stored identity
  const newIdentity = { email: currentUser.email, role: currentUser.role };
  
  if (lastKnownIdentity && lastKnownIdentity.email) {
    const oldEmail = lastKnownIdentity.email.toLowerCase().trim();
    const newEmail = currentUser.email.toLowerCase().trim();
    
    if (oldEmail !== newEmail) {
      console.log('[AuthSignal] ⚠️ IDENTITY MISMATCH DETECTED!');
      console.log('[AuthSignal] Old:', lastKnownIdentity.email, 'New:', currentUser.email);
      return true;
    }
  }

  // Update last known identity
  lastKnownIdentity = newIdentity;
  try {
    localStorage.setItem(AUTH_IDENTITY_KEY, JSON.stringify(newIdentity));
  } catch (e) {
    // Ignore
  }

  return false;
}

/**
 * Subscribe to auth version changes from other tabs
 * @param {function} callback - Function to call when auth changes: (payload) => void
 * @returns {function} Unsubscribe function
 */
export function subscribeToAuthChanges(callback) {
  const handlers = [];

  // Handler for BroadcastChannel messages (instant)
  // CRITICAL: Filter out self-sent messages to prevent login redirect loop
  if (broadcastChannel) {
    const bcHandler = (event) => {
      // Skip messages sent by THIS tab - prevents self-destructive auth reset loop
      // BroadcastChannel.postMessage() delivers to ALL subscribers including sender
      if (event.data?.senderId === TAB_ID) {
        console.log('[AuthSignal] Ignoring self-sent BroadcastChannel message');
        return;
      }
      console.log('[AuthSignal] Received via BroadcastChannel from another tab:', event.data);
      callback(event.data);
    };
    broadcastChannel.addEventListener('message', bcHandler);
    handlers.push(() => broadcastChannel.removeEventListener('message', bcHandler));
  }

  // Handler for localStorage 'storage' events (fires only in OTHER tabs - no filtering needed)
  const storageHandler = (event) => {
    // Listen for auth version changes (primary)
    if (event.key === AUTH_VERSION_KEY && event.newValue) {
      console.log('[AuthSignal] Auth version changed via storage event');
      // Fetch the full payload
      try {
        const payloadStr = localStorage.getItem(STORAGE_KEY);
        const payload = payloadStr ? JSON.parse(payloadStr) : { version: event.newValue };
        callback(payload);
      } catch (e) {
        callback({ version: event.newValue });
      }
      return;
    }
    
    // Legacy: also listen for payload changes
    if (event.key === STORAGE_KEY && event.newValue) {
      try {
        const payload = JSON.parse(event.newValue);
        console.log('[AuthSignal] Received via localStorage:', payload);
        callback(payload);
      } catch (e) {
        console.error('[AuthSignal] Failed to parse storage event:', e);
      }
    }
  };
  window.addEventListener('storage', storageHandler);
  handlers.push(() => window.removeEventListener('storage', storageHandler));

  // Return unsubscribe function
  return () => {
    handlers.forEach(unsub => unsub());
  };
}

/**
 * Get the correct home route for a given role
 * @param {string} role - User role
 * @returns {string} Route path
 */
export function getHomeRouteForRole(role) {
  switch (role) {
    case 'technician':
      return '/tech';
    case 'platform_admin':
      return '/admin';
    case 'tenant_owner':
    case 'admin':
    default:
      return '/dashboard';
  }
}

/**
 * Check if the current path is valid for the given role
 * @param {string} role - User role
 * @param {string} path - Current pathname
 * @returns {boolean} Whether the path is valid for this role
 */
export function isValidPathForRole(role, path) {
  switch (role) {
    case 'technician':
      // Technicians can only access /tech/* paths and public paths
      return path.startsWith('/tech') || path.startsWith('/public') || path === '/';
    case 'platform_admin':
      // Platform admins can access /admin/*
      return path.startsWith('/admin');
    case 'tenant_owner':
    case 'admin':
      // Contractors can access everything except /admin/*
      return !path.startsWith('/admin');
    default:
      return true;
  }
}

/**
 * Handle auth change from another tab
 * This is the CRITICAL function that prevents stale data
 * @param {string} apiUrl - API base URL
 */
export async function handleCrossTabAuthChange(apiUrl) {
  console.log('[AuthSignal] 🔄 Handling cross-tab auth change...');
  
  // 1. CRITICAL: Purge ALL cached data first
  hardAuthReset();
  
  // 2. Check current auth state
  try {
    const token = localStorage.getItem('auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    const response = await fetch(`${apiUrl}/auth/me`, {
      credentials: 'include',
      headers,
    });
    
    if (!response.ok) {
      // Not authenticated - redirect to login
      console.log('[AuthSignal] Not authenticated, redirecting to login');
      window.location.replace('/');
      return;
    }
    
    const user = await response.json();
    const targetRoute = getHomeRouteForRole(user.role);
    
    console.log('[AuthSignal] New identity:', user.email, 'role:', user.role);
    console.log('[AuthSignal] Redirecting to:', targetRoute);
    
    // 3. Redirect to correct shell
    window.location.replace(targetRoute);
    
  } catch (error) {
    console.error('[AuthSignal] Failed to check auth:', error);
    // On error, redirect to login for safety
    window.location.replace('/');
  }
}

// Legacy export for backward compatibility
export function broadcastAuthChange(event, data = {}) {
  bumpAuthVersion(event, data.email ? { email: data.email, role: data.role } : null);
}

export function forceIdentityReload() {
  hardAuthReset();
  window.location.reload();
}

export default {
  initAuthSignal,
  bumpAuthVersion,
  hardAuthReset,
  checkIdentityMismatch,
  subscribeToAuthChanges,
  handleCrossTabAuthChange,
  getHomeRouteForRole,
  isValidPathForRole,
  broadcastAuthChange,
  forceIdentityReload,
};
